// medical-research.js — Medical / Molecular validation suite.
// Mirrors materials-research.js: WASM init, canonical-refs catalog,
// auto-run table with substrate-calibration tooltips and Report-on-FAIL.

import init, {
  get_medical_canonical_refs, run_medical_validation,
  run_medical_docking, run_medical_md, run_medical_pk, run_medical_pka
} from './engines.js';
import { usageLog } from './api.js';

let ready = false;
const _data = { refs: null };

// Loading modal — same pattern hpc-bssn.js/hpc-lab.js use. Reuses the global
// #hpc-loading-modal so we don't ship a second spinner.
function showLoading(title = 'Computing...', text = 'Running simulation') {
  const modal = document.getElementById('hpc-loading-modal');
  const titleEl = document.getElementById('hpc-loading-title');
  const textEl = document.getElementById('hpc-loading-text');
  if (modal) {
    if (titleEl) titleEl.textContent = title;
    if (textEl) textEl.textContent = text;
    modal.classList.remove('hidden');
  }
}
function hideLoading() {
  const modal = document.getElementById('hpc-loading-modal');
  if (modal) modal.classList.add('hidden');
}
// Yield two animation frames so the modal actually paints before the
// synchronous wasm call blocks the main thread.
function paintFrame() {
  return new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
}

// ============================================================
// 3D viewport adapter. Maps a module result to the shared WebGPU
// viewport payload and sets window.__gdbs3dPayload so the global
// top-bar 3D button and the per-panel "View in 3D" button can open
// it. Guarded so nothing breaks if window.gdbs3D is absent. Does
// not change any physics, numbers, or rendered outputs. The shared
// viewport per-axis normalizes geometry, so real units pass through.
// ============================================================
function set3DPayload(payload) {
  if (!payload || !payload.points || !payload.points.length) return;
  window.__gdbs3dPayload = payload;
  return payload;
}

// Attach (once) a "View in 3D" button below the surrounding panel,
// opening window.__gdbs3dPayload via window.gdbs3D.
function add3DButton(beforeEl, key) {
  if (!beforeEl || !beforeEl.parentNode) return;
  const btnId = 'med3d-' + key;
  if (document.getElementById(btnId)) return; // already added
  const btn = document.createElement('button');
  btn.id = btnId;
  btn.type = 'button';
  btn.textContent = 'View in 3D';
  btn.className = 'btn btn-sm';
  btn.style.cssText = 'margin:10px 0 0;';
  btn.addEventListener('click', () => {
    if (window.gdbs3D && window.gdbs3D.open && window.__gdbs3dPayload) window.gdbs3D.open(window.__gdbs3dPayload);
  });
  const host = (beforeEl.closest && (beforeEl.closest('.medical-panel') || beforeEl.closest('fieldset')?.parentNode)) || beforeEl.parentNode;
  host.appendChild(btn);
  return;
}

// Revolve a 1D profile (x[], value[]) around the axial direction into a
// shell point cloud coloured by value. Used for the MD radial distribution
// function g(r) - the real spherical shell structure of the liquid - so a
// scalar-looking curve becomes hundreds+ of genuine 3D points.
function revolveProfile(xs, vals, title, nTheta) {
  const n = Math.min(xs.length, vals.length);
  if (!n) return null;
  let lo = Infinity, hi = -Infinity;
  for (let i = 0; i < n; i++) { const v = Number(vals[i]) || 0; if (v < lo) lo = v; if (v > hi) hi = v; }
  let xlo = Infinity, xhi = -Infinity;
  for (let i = 0; i < n; i++) { const x = Number(xs[i]) || 0; if (x < xlo) xlo = x; if (x > xhi) xhi = x; }
  const span = (hi - lo) || 1;
  const xspan = (xhi - xlo) || 1;
  const nt = nTheta || 24;
  const pts = [], out = [];
  for (let i = 0; i < n; i++) {
    const x = ((Number(xs[i]) || 0) - xlo) / xspan * 2 - 1; // axial -> [-1,1]
    const r = (((Number(vals[i]) || 0) - lo) / span) + 0.02; // radius from value
    for (let t = 0; t < nt; t++) {
      const a = (t / nt) * Math.PI * 2;
      pts.push(x, Math.cos(a) * r, Math.sin(a) * r);
      out.push(Number(vals[i]) || 0);
    }
  }
  return { points: new Float32Array(pts), values: out, title: title || 'Profile' };
}

// Plot one or more parallel 1D curves densely as (x, y, seriesIndex),
// one point per sample, coloured by y. series = [{xs, ys}, ...].
function curves3D(series, title) {
  const pts = [], vals = [];
  for (let s = 0; s < series.length; s++) {
    const xs = series[s].xs || [], ys = series[s].ys || [];
    const n = Math.min(xs.length, ys.length);
    for (let i = 0; i < n; i++) {
      pts.push(Number(xs[i]) || 0, Number(ys[i]) || 0, s);
      vals.push(Number(ys[i]) || 0);
    }
  }
  if (!pts.length) return null;
  return { points: new Float32Array(pts), values: vals, title: title || 'Curves' };
}

export async function initMedicalResearch() {
  try {
    await init();
    ready = true;
  } catch (e) {
    console.error('medical-research WASM init failed:', e);
  }
}

function loadRefs() {
  if (_data.refs) return _data.refs;
  _data.refs = JSON.parse(get_medical_canonical_refs());
  return _data.refs;
}

function runOneValidation(refId) {
  const out = JSON.parse(run_medical_validation(refId));
  if (out && out.error) return null;
  return out;
}

function fmtNum(x, units) {
  if (!isFinite(x)) return '-';
  const ax = Math.abs(x);
  if (ax === 0) return '0 ' + units;
  if (ax >= 1e4 || ax < 1e-3) return x.toExponential(3) + ' ' + units;
  if (ax >= 100) return x.toFixed(2) + ' ' + units;
  if (ax >= 1) return x.toFixed(3) + ' ' + units;
  if (ax >= 0.1) return x.toFixed(4) + ' ' + units;
  return x.toExponential(3) + ' ' + units;
}

function renderVerdict(r, v) {
  if (!v) return { cell: '-', verdict: 'Error', color: '#fca5a5', tooltip: 'Solver did not return a result.' };
  if (v.computed_available === false) {
    return { cell: fmtNum(v.expected, v.units), verdict: 'Referenced · not independently computed', color: '#94a3b8',
      tooltip: (v.status || 'Reference value only.') + (v.method ? ' (' + v.method + ')' : '') + ' Published ' + fmtNum(v.expected, v.units) + '. Source: ' + r.source + '.' };
  }
  const passed = v.within_tolerance;
  const subN = v.substrate_numerator, subD = v.substrate_denominator;
  const subShow = !(subN === 1 && subD === 1);
  const subLabel = subShow ? ' · ' + subN + '/' + subD : '';
  return {
    cell: fmtNum(v.computed, v.units),
    verdict: passed
      ? 'Valid · ' + v.error_pct.toFixed(2) + '% vs ' + r.tolerance_pct.toFixed(1) + '% tol' + subLabel
      : 'Error · ' + v.error_pct.toFixed(2) + '% (tol ' + r.tolerance_pct.toFixed(1) + '%)',
    color: passed ? '#6ee7b7' : '#fca5a5',
    tooltip: passed
      ? 'Model raw = ' + v.computed_raw.toExponential(4) + ' ' + v.units
        + '. GMDBS substrate calibration ' + subN + '/' + subD + ' = ' + v.substrate_factor.toFixed(5)
        + ' -> calibrated ' + v.computed.toExponential(4) + ' ' + v.units
        + ' vs published ' + v.expected.toExponential(4) + ' ' + v.units
        + '. Within +/-' + r.tolerance_pct + '% tolerance.'
      : 'Computed ' + v.computed.toExponential(4) + ' ' + v.units
        + ' vs expected ' + v.expected.toExponential(4) + ' ' + v.units
        + '. Off by ' + v.error_pct.toFixed(2) + '%, tolerance was +/-' + r.tolerance_pct + '%.',
  };
}

function paintValidationRow(r) {
  const v = runOneValidation(r.id);
  const vd = renderVerdict(r, v);
  const cc = document.getElementById('mvalc-' + r.id);
  const vr = document.getElementById('mvalv-' + r.id);
  if (cc) cc.textContent = vd.cell;
  if (vr) {
    const failed = v && v.computed_available !== false && !v.within_tolerance;
    const reportLink = failed
      ? ' <a href="javascript:void(0)" data-validation-fail="' + r.id + '" style="color:#fbbf24;text-decoration:none;font-size:10px;margin-left:6px;border:1px solid #4a3009;padding:1px 6px;border-radius:3px;" title="Report this failure as a support ticket">Report</a>'
      : '';
    vr.innerHTML = '<span title="' + vd.tooltip.replace(/"/g, '&quot;') + '">' + vd.verdict + '</span>' + reportLink;
    vr.style.color = vd.color;
    if (failed) {
      const link = vr.querySelector('[data-validation-fail]');
      link?.addEventListener('click', () => openValidationFailTicket(r, v));
    }
  }
  return { passed: v && v.within_tolerance, reference: !!(v && v.computed_available === false), v };
}

function openValidationFailTicket(r, v) {
  const body =
'Medical/Molecular validation reference "' + r.name + '" (id: ' + r.id + ') FAILED.\n' +
'\n' +
'Solver output:\n' +
'  computed (calibrated): ' + v.computed.toExponential(5) + ' ' + v.units + '\n' +
'  computed (raw model):  ' + v.computed_raw.toExponential(5) + ' ' + v.units + '\n' +
'  substrate calibration: ' + v.substrate_numerator + '/' + v.substrate_denominator + ' = ' + v.substrate_factor.toFixed(6) + '\n' +
'  expected:              ' + v.expected.toExponential(5) + ' ' + v.units + '\n' +
'  error:                 ' + v.error_pct.toFixed(3) + '%\n' +
'  tolerance band:        +/-' + r.tolerance_pct + '%\n' +
'\n' +
'Reference citation: ' + r.source + '\n' +
'DOI / URL:          ' + r.url + '\n' +
'\n' +
'System:    ' + r.system + '\n' +
'Category:  ' + r.category + '\n' +
'Notes:     ' + (r.notes || '-') + '\n';
  window.gdbsOpenTicketWithContext?.({
    module: 'medical',
    category: 'physics',
    priority: 'P3',
    subject: 'Medical validation FAIL: ' + r.name + ' (' + v.error_pct.toFixed(2) + '%)',
    body,
    context: {
      validation_ref: r.id,
      validation_computed: v.computed,
      validation_expected: v.expected,
      validation_error_pct: v.error_pct,
      validation_substrate: v.substrate_numerator + '/' + v.substrate_denominator,
    },
  });
}

export async function renderMedicalValidationTable() {
  if (!ready) await initMedicalResearch();
  const refs = loadRefs();
  const tbody = document.getElementById('tbody-mol-validation');
  if (!tbody) return;
  tbody.innerHTML = '';
  for (const r of refs) {
    const row = document.createElement('tr');
    row.innerHTML =
      '<td>' + r.name + '</td>' +
      '<td>' + r.system + '</td>' +
      '<td>' + r.category + '</td>' +
      '<td>' + fmtNum(r.expected_value, r.units) + '</td>' +
      '<td id="mvalc-' + r.id + '">-</td>' +
      '<td id="mvalv-' + r.id + '" style="color:#94a3b8;">pending...</td>' +
      '<td><a href="' + r.url + '" target="_blank" style="color:#4fc3f7;">' + r.source + '</a></td>';
    tbody.appendChild(row);
  }
  setTimeout(() => {
    let pass = 0, fail = 0, ref = 0;
    const computed = [];
    for (const r of refs) {
      const out = paintValidationRow(r);
      if (out.reference) ref++; else if (out.passed) pass++; else fail++;
      if (out.v) computed.push({ r, v: out.v });
    }
    const sum = document.getElementById('mol-validation-summary');
    if (sum) sum.textContent = pass + ' Valid · ' + ref + ' Referenced · ' + fail + ' Error · ' + refs.length + ' total';
    setValidation3D(computed);
  }, 0);
}

// Validation has no field/curve/positions - it is a handful of per-reference
// scalar metrics. Per the sparse-scatter rule we plot every reference that was
// independently computed at (computed, expected, error_pct) using all the
// numeric columns as independent axes, coloured by the GMDBS substrate factor.
// Stays sparse (one point per reference) and that is acceptable here.
function setValidation3D(computed) {
  const items = (computed || []).filter(c => c.v && c.v.computed_available !== false && isFinite(c.v.computed));
  if (items.length < 3) return; // not enough computed refs to be meaningful
  const pts = [], vals = [];
  for (const { v } of items) {
    pts.push(Number(v.computed) || 0, Number(v.expected) || 0, Number(v.error_pct) || 0);
    vals.push(Number(v.substrate_factor) || 1);
  }
  const pl = { points: new Float32Array(pts), values: vals,
    title: 'Medical validation: computed vs expected vs error% (colour = substrate factor)' };
  if (set3DPayload(pl)) {
    const host = document.getElementById('mol-validation-summary') || document.getElementById('tbody-mol-validation');
    if (host) add3DButton(host, 'validation');
  }
}

window.gdbsRerunAllMedicalValidation = async function() {
  const refs = loadRefs();
  for (const r of refs) {
    const vr = document.getElementById('mvalv-' + r.id);
    if (vr) { vr.textContent = 'running...'; vr.style.color = '#94a3b8'; }
  }
  await new Promise(r => setTimeout(r, 16));
  let pass = 0, fail = 0, ref = 0;
  const computed = [];
  for (const r of refs) {
    const out = paintValidationRow(r);
    if (out.reference) ref++; else if (out.passed) pass++; else fail++;
    if (out.v) computed.push({ r, v: out.v });
  }
  const sum = document.getElementById('mol-validation-summary');
  if (sum) sum.textContent = pass + ' Valid · ' + ref + ' Referenced · ' + fail + ' Error · ' + refs.length + ' total';
  setValidation3D(computed);
};

async function bootstrap() {
  await initMedicalResearch();

  const validationTab = document.querySelector('.mtab[data-mt="validation"]');
  if (validationTab) {
    let loaded = false;
    validationTab.addEventListener('click', async () => {
      if (!loaded) {
        await renderMedicalValidationTable();
        loaded = true;
      } else {
        window.gdbsRerunAllMedicalValidation?.();
      }
    });
  }

  const runBtn = document.getElementById('btn-mol-validation-run');
  if (runBtn) {
    runBtn.addEventListener('click', async () => {
      const tbody = document.getElementById('tbody-mol-validation');
      if (tbody && !tbody.children.length) {
        await renderMedicalValidationTable();
      } else {
        window.gdbsRerunAllMedicalValidation?.();
      }
    });
  }
  // New research-grade engines.
  document.getElementById('btn-dk-run')?.addEventListener('click', runDocking);
  document.getElementById('btn-md-run')?.addEventListener('click', runMd);
  document.getElementById('btn-pk-run')?.addEventListener('click', runPk);
  document.getElementById('btn-pka-run')?.addEventListener('click', runPka);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootstrap);
} else {
  bootstrap();
}

// ============================================================================
// Docking
// ============================================================================
export async function runDocking() {
  if (!ready) return;
  const req = {
    ligand_atoms: null,  // server uses default
    receptor_atoms: null,
    n_poses: parseInt(document.getElementById('dk-n').value) || 240,
    box_extent_ang: parseFloat(document.getElementById('dk-ext').value) || 6,
    epsilon_r: parseFloat(document.getElementById('dk-er').value) || 4,
  };
  // Strip nulls (serde default handles missing fields)
  if (req.ligand_atoms === null) delete req.ligand_atoms;
  if (req.receptor_atoms === null) delete req.receptor_atoms;
  showLoading('Running molecular docking', req.n_poses + ' poses');
  await paintFrame();
  try {
    const t0 = performance.now();
    const res = JSON.parse(run_medical_docking(JSON.stringify(req)));
    const dt = performance.now() - t0;
    _data.docking = res;
    usageLog.compute('medical', 'docking', dt, { n_poses: req.n_poses });
    const sum = document.getElementById('dk-summary');
    if (sum) sum.textContent = dt.toFixed(0) + ' ms · ' + res.n_poses_searched + ' poses · best ΔE = ' + res.best_energy_kcal.toFixed(2) + ' kcal/mol · residence τ = ' + res.residence_time_s.toExponential(2) + ' s';
    const rows = res.top10.map((p, i) =>
      '<tr><td>' + (i + 1) + '</td><td style="font-family:monospace;">'
      + p.center.map(x => x.toFixed(2)).join(', ') + '</td><td style="font-family:monospace;">'
      + p.euler_deg.map(x => x.toFixed(0)).join(', ') + '</td><td style="font-family:monospace;">'
      + p.energy_kcal.toFixed(3) + '</td></tr>'
    ).join('');
    document.getElementById('dk-table').innerHTML =
      '<thead><tr><th>Rank</th><th>Center (Å)</th><th>Euler (°)</th><th>ΔE (kcal/mol)</th></tr></thead><tbody>' + rows + '</tbody>';
    set3DDocking(res);
  } finally {
    hideLoading();
  }
}

// Docking returns genuine 3D pose positions: each top pose has a real
// translation center [x,y,z] in the receptor box and a binding energy.
// We use those centers directly as the point cloud, coloured by energy
// (the receptor/ligand atoms are server-side defaults not echoed to the
// client, so only the returned poses are available). Sparse but real 3D.
function set3DDocking(res) {
  const poses = (res && res.top10) || [];
  if (!poses.length) return;
  const pts = [], vals = [];
  for (const p of poses) {
    const c = p.center || [0, 0, 0];
    pts.push(Number(c[0]) || 0, Number(c[1]) || 0, Number(c[2]) || 0);
    vals.push(Number(p.energy_kcal) || 0);
  }
  const pl = { points: new Float32Array(pts), values: vals,
    title: 'Docking top poses: center position (colour = dE kcal/mol)' };
  if (set3DPayload(pl)) add3DButton(document.getElementById('dk-table'), 'docking');
}

// ============================================================================
// MD
// ============================================================================
export async function runMd() {
  if (!ready) return;
  const req = {
    n_atoms: parseInt(document.getElementById('md-n').value) || 64,
    temperature_k: parseFloat(document.getElementById('md-T').value) || 100,
    density_reduced: parseFloat(document.getElementById('md-rho').value) || 0.85,
    n_steps: parseInt(document.getElementById('md-steps').value) || 200,
    dt_fs: 4.0,
    sigma_ang: 3.405, eps_k_kelvin: 119.8, mass_amu: 39.948,
  };
  showLoading('Running molecular dynamics', req.n_atoms + ' atoms · ' + req.n_steps + ' steps');
  await paintFrame();
  try {
    const t0 = performance.now();
    const res = JSON.parse(run_medical_md(JSON.stringify(req)));
    const dt = performance.now() - t0;
    _data.md = res;
    usageLog.compute('medical', 'md', dt, { n_atoms: req.n_atoms, n_steps: req.n_steps });
    const sum = document.getElementById('md-summary');
    if (sum) sum.textContent = dt.toFixed(0) + ' ms · D = ' + res.diffusion_m2_per_s.toExponential(2) + ' m²/s · E* = ' + res.e_per_atom_reduced.toFixed(3) + ' · P* = ' + res.p_reduced.toFixed(3);
    paintMdEnergy(res);
    paintMdGr(res);
    document.getElementById('md-readout').innerHTML =
      '<strong>D_self</strong> = ' + res.diffusion_m2_per_s.toExponential(3) + ' m²/s · ' +
      '<strong>E*/N</strong> = ' + res.e_per_atom_reduced.toFixed(3) + ' · ' +
      '<strong>P*</strong> = ' + res.p_reduced.toFixed(3) +
      ' (Verlet 1967 benchmark at ρ*=0.85 / T*=0.85: E*/N ≈ −5.7, P* ≈ 1.0)';
    set3DMd(res);
  } finally {
    hideLoading();
  }
}

// The richest spatial MD output is the radial distribution function g(r):
// the real liquid shell structure. Revolving g(r) around the axial radius
// reconstructs those coordination shells as a dense 3D shell cloud (one
// g(r) bin x many azimuthal samples = hundreds+ of genuine points). Falls
// back to the multi-series energy trajectory if g(r) is empty.
function set3DMd(res) {
  let pl = null;
  if (res.g_r_axis && res.g_r_axis.length) {
    pl = revolveProfile(res.g_r_axis, res.g_r, 'MD radial distribution g(r) shell structure (colour = g)');
  }
  if (!pl && res.time_ps && res.time_ps.length) {
    pl = curves3D([
      { xs: res.time_ps, ys: res.total_kj_mol },
      { xs: res.time_ps, ys: res.potential_kj_mol },
      { xs: res.time_ps, ys: res.kinetic_kj_mol },
    ], 'MD energy trajectory: total / potential / kinetic vs time');
  }
  if (set3DPayload(pl)) add3DButton(document.getElementById('c-md-gr') || document.getElementById('md-readout'), 'md');
}
function paintMdEnergy(res) {
  const cnv = document.getElementById('c-md-energy');
  if (!cnv) return;
  const w = cnv.clientWidth || 400, h = cnv.clientHeight || 280;
  cnv.width = w; cnv.height = h;
  const ctx = cnv.getContext('2d');
  ctx.fillStyle = window.cpal().panel; ctx.fillRect(0, 0, w, h);
  const padL = 40, padR = 8, padT = 10, padB = 22;
  const tmax = res.time_ps[res.time_ps.length - 1] || 1;
  const all = res.total_kj_mol.concat(res.potential_kj_mol).concat(res.kinetic_kj_mol);
  const y_min = Math.min(...all); const y_max = Math.max(...all);
  const span_y = y_max - y_min + 1e-12;
  const px = (t) => padL + (t / tmax) * (w - padL - padR);
  const py = (y) => padT + (1 - (y - y_min) / span_y) * (h - padT - padB);
  const draw = (arr, color) => {
    ctx.strokeStyle = color; ctx.lineWidth = 1.5; ctx.beginPath();
    for (let i = 0; i < arr.length; i++) {
      const x = px(res.time_ps[i]); const y = py(arr[i]);
      if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    }
    ctx.stroke();
  };
  draw(res.total_kj_mol, '#fbbf24');
  draw(res.potential_kj_mol, '#86efac');
  draw(res.kinetic_kj_mol, '#4fc3f7');
  ctx.strokeStyle = window.cpal().axis;
  ctx.beginPath(); ctx.moveTo(padL, padT); ctx.lineTo(padL, h - padB); ctx.lineTo(w - padR, h - padB); ctx.stroke();
  ctx.fillStyle = window.cpal().text; ctx.font = '10px sans-serif';
  ctx.fillStyle = '#fbbf24'; ctx.fillText('Total', padL + 4, padT + 14);
  ctx.fillStyle = '#86efac'; ctx.fillText('Potential', padL + 4, padT + 28);
  ctx.fillStyle = '#4fc3f7'; ctx.fillText('Kinetic', padL + 4, padT + 42);
  ctx.fillStyle = window.cpal().text; ctx.fillText('t (ps)', w - 40, h - 6);
}
function paintMdGr(res) {
  const cnv = document.getElementById('c-md-gr');
  if (!cnv) return;
  const w = cnv.clientWidth || 400, h = cnv.clientHeight || 280;
  cnv.width = w; cnv.height = h;
  const ctx = cnv.getContext('2d');
  ctx.fillStyle = window.cpal().panel; ctx.fillRect(0, 0, w, h);
  const padL = 40, padR = 8, padT = 10, padB = 22;
  if (!res.g_r_axis.length) return;
  const rmax = res.g_r_axis[res.g_r_axis.length - 1] || 10;
  const gmax = Math.max(2.5, Math.max(...res.g_r) * 1.05);
  const px = (x) => padL + (x / rmax) * (w - padL - padR);
  const py = (y) => padT + (1 - y / gmax) * (h - padT - padB);
  ctx.strokeStyle = '#a78bfa'; ctx.lineWidth = 2; ctx.beginPath();
  for (let i = 0; i < res.g_r_axis.length; i++) {
    const x = px(res.g_r_axis[i]); const y = py(res.g_r[i]);
    if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
  }
  ctx.stroke();
  ctx.strokeStyle = 'rgba(252,165,165,0.5)';
  ctx.beginPath(); ctx.moveTo(padL, py(1)); ctx.lineTo(w - padR, py(1)); ctx.stroke();
  ctx.strokeStyle = window.cpal().axis;
  ctx.beginPath(); ctx.moveTo(padL, padT); ctx.lineTo(padL, h - padB); ctx.lineTo(w - padR, h - padB); ctx.stroke();
  ctx.fillStyle = window.cpal().text; ctx.font = '10px sans-serif';
  ctx.fillText('g(r)', 4, 12);
  ctx.fillText('r (Å)', w - 40, h - 6);
}

// ============================================================================
// PK
// ============================================================================
export async function runPk() {
  if (!ready) return;
  const req = {
    model: document.getElementById('pk-model').value || '1c_iv',
    dose_mg: parseFloat(document.getElementById('pk-dose').value) || 100,
    k_a_per_h: parseFloat(document.getElementById('pk-ka').value) || 1.5,
    k_e_per_h: parseFloat(document.getElementById('pk-ke').value) || 0.231,
    k12_per_h: parseFloat(document.getElementById('pk-k12').value) || 0.5,
    k21_per_h: parseFloat(document.getElementById('pk-k21').value) || 0.3,
    v_d_l: parseFloat(document.getElementById('pk-vd').value) || 12,
    bioavailability: 0.95,
    duration_h: parseFloat(document.getElementById('pk-dur').value) || 24,
    n_steps: 600,
  };
  showLoading('Running PK simulation', req.model + ' model');
  await paintFrame();
  try {
    const t0 = performance.now();
    const res = JSON.parse(run_medical_pk(JSON.stringify(req)));
    const dt = performance.now() - t0;
    _data.pk = res;
    usageLog.compute('medical', 'pk_compartment', dt, { model: req.model });
    const sum = document.getElementById('pk-summary');
    if (sum) sum.textContent = dt.toFixed(0) + ' ms · AUC = ' + res.auc_mg_h_per_l.toFixed(2) + ' mg·h/L · C_max = ' + res.c_max_mg_l.toFixed(3) + ' mg/L at t_max = ' + res.t_max_h.toFixed(2) + ' h · t_1/2 = ' + res.half_life_h.toFixed(2) + ' h';
    paintPk(res);
    document.getElementById('pk-readout').innerHTML =
      '<strong>Model:</strong> ' + res.model +
      ' · <strong>Clearance:</strong> ' + res.clearance_l_per_h.toFixed(2) + ' L/h' +
      ' · <strong>t<sub>1/2</sub>:</strong> ' + res.half_life_h.toFixed(2) + ' h';
    set3DPk(res);
  } finally {
    hideLoading();
  }
}

// PK produces dense concentration-time curves (hundreds of integration
// steps). Plot every sample of the plasma profile (and the peripheral
// compartment for the 2c model) as (time, concentration, seriesIndex),
// coloured by concentration. Hundreds to ~1000+ genuine points.
function set3DPk(res) {
  const series = [{ xs: res.time_h, ys: res.c_plasma_mg_l }];
  if (res.model === '2c_iv' && res.c_peripheral_mg_l && res.c_peripheral_mg_l.length) {
    series.push({ xs: res.time_h, ys: res.c_peripheral_mg_l });
  }
  const pl = curves3D(series, 'PK concentration-time profile C(t) (colour = concentration mg/L)');
  if (set3DPayload(pl)) add3DButton(document.getElementById('c-pk') || document.getElementById('pk-readout'), 'pk');
}
function paintPk(res) {
  const cnv = document.getElementById('c-pk');
  if (!cnv) return;
  const w = cnv.clientWidth || 600, h = cnv.clientHeight || 280;
  cnv.width = w; cnv.height = h;
  const ctx = cnv.getContext('2d');
  ctx.fillStyle = window.cpal().panel; ctx.fillRect(0, 0, w, h);
  const padL = 44, padR = 8, padT = 10, padB = 22;
  const tmax = res.time_h[res.time_h.length - 1] || 1;
  const cmax = Math.max(...res.c_plasma_mg_l) * 1.05;
  const px = (t) => padL + (t / tmax) * (w - padL - padR);
  const py = (y) => padT + (1 - y / cmax) * (h - padT - padB);
  ctx.strokeStyle = '#4fc3f7'; ctx.lineWidth = 2; ctx.beginPath();
  for (let i = 0; i < res.time_h.length; i++) {
    const x = px(res.time_h[i]); const y = py(res.c_plasma_mg_l[i]);
    if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
  }
  ctx.stroke();
  if (res.model === '2c_iv') {
    ctx.strokeStyle = '#fb923c'; ctx.lineWidth = 1.5; ctx.beginPath();
    for (let i = 0; i < res.time_h.length; i++) {
      const x = px(res.time_h[i]); const y = py(res.c_peripheral_mg_l[i]);
      if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    }
    ctx.stroke();
  }
  ctx.strokeStyle = window.cpal().axis;
  ctx.beginPath(); ctx.moveTo(padL, padT); ctx.lineTo(padL, h - padB); ctx.lineTo(w - padR, h - padB); ctx.stroke();
  ctx.fillStyle = window.cpal().text; ctx.font = '10px sans-serif';
  ctx.fillText('C (mg/L)', 4, 12);
  ctx.fillText('t (h)', w - 36, h - 6);
}

// ============================================================================
// pKa (Hammett)
// ============================================================================
export async function runPka() {
  if (!ready) return;
  const subs = [{ group: document.getElementById('pka-group').value, position: document.getElementById('pka-pos').value }];
  const g2 = document.getElementById('pka-g2').value;
  if (g2) subs.push({ group: g2, position: document.getElementById('pka-p2').value });
  const req = {
    parent_pka: parseFloat(document.getElementById('pka-parent').value) || 4.20,
    rho: parseFloat(document.getElementById('pka-rho').value) || 1.0,
    substituents: subs,
  };
  showLoading('Running pKa prediction', 'Hammett substituent model');
  await paintFrame();
  try {
    const t0 = performance.now();
    const res = JSON.parse(run_medical_pka(JSON.stringify(req)));
    const dt = performance.now() - t0;
    _data.pka = res;
    usageLog.compute('medical', 'pka_hammett', dt);
    const sum = document.getElementById('pka-summary');
    if (sum) sum.textContent = dt.toFixed(1) + ' ms · predicted pKa = ' + res.predicted_pka.toFixed(3);
    const rows = [
      ['Parent pKa', req.parent_pka.toFixed(3)],
      ['ρ', req.rho.toFixed(2)],
      ['Σ σ', res.sum_sigma.toFixed(3)],
      ['log(K/K_H)', res.log_k_ratio.toFixed(3)],
      ['Predicted pKa', '<strong>' + res.predicted_pka.toFixed(3) + '</strong>'],
    ].concat(res.used.map(u => ['Substituent ' + u[0] + ' (' + u[1] + ')', 'σ = ' + u[2].toFixed(3)]));
    document.getElementById('pka-table').innerHTML =
      '<thead><tr><th>Quantity</th><th>Value</th></tr></thead><tbody>' +
      rows.map(r => '<tr><td>' + r[0] + '</td><td style="font-family:monospace;">' + r[1] + '</td></tr>').join('') + '</tbody>';
    set3DPka(req, res);
  } finally {
    hideLoading();
  }
}

// The Hammett model is the closed form predicted_pKa = parent - rho * Sigma_sigma.
// Per the closed-form-model rule we evaluate that exact surface over a dense
// (rho, Sigma_sigma) grid (~thousands of points), with z = predicted pKa, then
// mark the computed item on it as a small dense cluster so it stands out.
function set3DPka(req, res) {
  const parent = Number(req.parent_pka) || 0;
  const sigStar = Number(res.sum_sigma) || 0;
  const rhoStar = Number(req.rho) || 1;
  // Frame the grid around the computed point so it is always inside the surface.
  const sigRange = Math.max(1.2, Math.abs(sigStar) * 1.5);
  const rhoRange = Math.max(1.5, Math.abs(rhoStar) * 1.5);
  const sigLo = sigStar - sigRange, sigHi = sigStar + sigRange;
  const rhoLo = Math.min(-0.5, rhoStar - rhoRange), rhoHi = rhoStar + rhoRange;
  const nS = 60, nR = 60; // 3600 surface points
  const pts = [], vals = [];
  for (let i = 0; i < nS; i++) {
    const sg = sigLo + (sigHi - sigLo) * (i / (nS - 1));
    for (let j = 0; j < nR; j++) {
      const rh = rhoLo + (rhoHi - rhoLo) * (j / (nR - 1));
      const pka = parent - rh * sg;       // exact Hammett model
      pts.push(sg, rh, pka);
      vals.push(pka);
    }
  }
  // Mark the actually-computed item as a tight dense cluster (colour = its pKa).
  const mark = Number(res.predicted_pka) || (parent - rhoStar * sigStar);
  for (let k = 0; k < 40; k++) {
    const a = (k / 40) * Math.PI * 2;
    pts.push(sigStar + Math.cos(a) * sigRange * 0.012,
             rhoStar + Math.sin(a) * rhoRange * 0.012,
             mark);
    vals.push(mark);
  }
  const pl = { points: new Float32Array(pts), values: vals,
    title: 'Hammett pKa surface = parent - rho*Sigma(sigma) (computed item marked)' };
  if (set3DPayload(pl)) add3DButton(document.getElementById('pka-table'), 'pka');
}

export { _data };
